/**
 * Product Personalisation Handler
 * Handles adding products with personalisation add-ons to cart
 * Groups items together using _groupUID property
 */

(function() {
  'use strict';

  window.ProductPersonalisation = {
    // Helper function to extract property value from cart item properties
    // Handles both array format [{name: 'key', value: 'value'}] and object format {key: 'value'}
    getPropertyValue: function(properties, key) {
      if (!properties) return '';
      
      if (Array.isArray(properties)) {
        for (const prop of properties) {
          const propName = prop.name || prop.first;
          if (propName === key) {
            return prop.value || prop.last || '';
          }
        }
      } else if (typeof properties === 'object') {
        return properties[key] || '';
      }
      
      return '';
    },

    // Helper function to get item metadata from cart item
    getItemMetadata: function(item) {
      const groupUID = this.getPropertyValue(item.properties, '_groupUID');
      const isMain = this.getPropertyValue(item.properties, '_isMainItem') === 'true';
      const isAddon = this.getPropertyValue(item.properties, '_isAddon') === 'true';
      
      return { groupUID, isMain, isAddon };
    },

    init: function() {
      const form = document.querySelector('.product-single__form');
      if (!form || form.classList.contains('personalisation-loaded')) return;

      // Check if personalisation is enabled by looking for addon containers
      const hasPersonalisation = document.querySelector('.product-addon-container-gm') || 
                                 document.querySelector('.product-academy-personalisation');
      
      if (!hasPersonalisation) return; // Don't intercept if no personalisation

      form.classList.add('personalisation-loaded');

      // Initialize state
      this.initState();
      this.bindEvents();
      this.bindFormSubmit(form);
    },

    initState: function() {
      window.personalisationState = {
        player: '',
        badge: 'no',
        sponsor: 'no',
        naam: '',
        nummer: '',
        initials: ''
      };
    },

    bindEvents: function() {
      // Player selection
      document.querySelectorAll('.player-select')?.forEach(select => {
        select.addEventListener('change', () => {
          window.personalisationState.player = select.value.trim();
        });
      });

      // Name input
      document.querySelector('.naam-input')?.addEventListener('input', e => {
        window.personalisationState.naam = e.target.value.trim();
      });

      // Number input
      document.querySelector('.nummer-input')?.addEventListener('input', e => {
        window.personalisationState.nummer = e.target.value.trim();
      });

      // Initials input (for academy)
      document.getElementById('initials')?.addEventListener('input', e => {
        window.personalisationState.initials = e.target.value.trim();
      });

      // Badge/Sponsor toggle buttons
      document.querySelectorAll('.product-addon-buttons')?.forEach(buttonGroup => {
        buttonGroup.addEventListener('click', event => {
          const clicked = event.target;
          if (!clicked.classList.contains('product-addon-button')) return;

          const value = clicked.getAttribute('data-value');
          buttonGroup.querySelectorAll('.product-addon-button').forEach(btn => btn.classList.remove('selected'));
          clicked.classList.add('selected');
          buttonGroup.setAttribute('data-selected', value);

          // Skip if it's initials toggle
          if (buttonGroup.classList.contains('initials-toggle-buttons')) return;

          const optionType = buttonGroup.getAttribute('data-option') || 'badge';
          window.personalisationState[optionType] = value;
        });
      });

      // Personalisation mode toggle buttons
      document.querySelectorAll('.personalisation-button')?.forEach(button => {
        button.addEventListener('click', () => {
          document.querySelectorAll('.personalisation-button').forEach(btn => btn.classList.remove('selected'));
          button.classList.add('selected');

          const target = button.getAttribute('data-target');
          document.querySelector('.product-addon-player-item')?.classList.add('hidden');
          document.querySelector('.product-addon-naamnummer-container')?.classList.add('hidden');

          if (target === 'player') {
            document.querySelector('.product-addon-player-item')?.classList.remove('hidden');
          } else if (target === 'naam-nummer') {
            document.querySelector('.product-addon-naamnummer-container')?.classList.remove('hidden');
          }
        });
      });
    },

    bindFormSubmit: function(form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        if (!addToCartBtn) return false;

        addToCartBtn.disabled = true;
        addToCartBtn.innerHTML = 'Toevoegen...';

        try {
          await this.addItemsToCart(form, addToCartBtn);
        } catch (err) {
          console.error('❌ Fout bij toevoegen:', err);
          alert('Er is iets mis gegaan bij het toevoegen van de items.');
        } finally {
          addToCartBtn.disabled = false;
          addToCartBtn.innerHTML = 'Toevoegen aan winkelwagen';
        }
        
        return false;
      }, true); // Use capture phase to ensure we catch it first
    },

    getMainProductDetails: function() {
      const mainProductTitle = document.querySelector('[data-product-title]')?.getAttribute('data-product-title') || 'Onbekend Shirt';
      const variantId = parseInt(document.querySelector('select[name="id"]')?.value);
      const selectedVariant = window.meta?.product?.variants?.find(v => v.id === variantId);

      return {
        model: mainProductTitle,
        size: selectedVariant?.public_title || selectedVariant?.name || 'Unknown'
      };
    },

    generateGroupUID: function() {
      return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    addItemsToCart: async function(form, addToCartBtn) {
      // Clean up any orphaned addons before adding new items
      await this.cleanupOrphanedAddons();

      // Detect active mode
      const activeMode = document.querySelector('.personalisation-button.selected')
                        ?.getAttribute('data-target') || 'none';

      // Get current values
      const player = document.querySelector('.player-select')?.value.trim() || '';
      const naam = document.querySelector('.naam-input')?.value.trim() || '';
      const nummer = document.querySelector('.nummer-input')?.value.trim() || '';
      const initials = document.getElementById('initials')?.value.trim() || '';

      const badge = document.querySelector('#badge-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const sponsor = document.querySelector('#sponsor-sleeve-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const initialsToggle = document.querySelector('.initials-toggle-buttons .product-academy-personalisation-button.selected')?.dataset?.value;

      // Validate initials if required
      if (initialsToggle === 'yes' && !initials) {
        alert('Vul je initialen in om verder te gaan.');
        return;
      }

      // Clear hidden field values
      if (activeMode === 'player') {
        const naamEl = document.querySelector('.naam-input');
        const nummerEl = document.querySelector('.nummer-input');
        if (naamEl) naamEl.value = '';
        if (nummerEl) nummerEl.value = '';
      } else if (activeMode === 'naam-nummer') {
        const playerSel = document.querySelector('.player-select');
        if (playerSel) playerSel.value = '';
      }

      const productDetails = this.getMainProductDetails();
      const shirtVariantId = form.querySelector('select[name="id"]')?.value;

      if (!shirtVariantId) {
        throw new Error('No variant selected');
      }

      // Generate unique group ID for this set of items
      const groupUID = this.generateGroupUID();
      const itemsToAdd = [];

      // Collect all personalisation properties for the main shirt
      const mainItemProperties = {
        Model: productDetails.model,
        Size: productDetails.size,
        _groupUID: groupUID,
        _isMainItem: 'true'
      };

      // Add personalisation properties based on active mode
      if (activeMode === 'player' && player) {
        mainItemProperties.Speler = player;
      } else if (activeMode === 'naam-nummer') {
        if (naam) mainItemProperties.Naam = naam;
        if (nummer) mainItemProperties.Nummer = nummer;
      }

      // Add badge and sponsor to main item properties
      if (badge === 'yes') {
        mainItemProperties.Badge = 'Eredivisie Badge';
      }
      if (sponsor === 'yes') {
        mainItemProperties.Sponsor = 'Sponsor Mouw';
      }
      if (initialsToggle === 'yes' && initials) {
        mainItemProperties['Initialen/nummer'] = initials;
      }

      // Add main product with all properties
      itemsToAdd.push({
        id: parseInt(shirtVariantId),
        quantity: 1,
        properties: mainItemProperties
      });

      // Add badge add-on (if selected)
      if (badge === 'yes') {
        const badgeProductId = document.getElementById('badge-product-addon')?.dataset?.productId;
        if (badgeProductId) {
          itemsToAdd.push({
            id: parseInt(badgeProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'badge'
            }
          });
        }
      }

      // Add sponsor sleeve add-on (if selected)
      if (sponsor === 'yes') {
        const sponsorProductId = document.getElementById('sponsor-sleeve-product-addon')?.dataset?.productId;
        if (sponsorProductId) {
          itemsToAdd.push({
            id: parseInt(sponsorProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'sponsor'
            }
          });
        }
      }

      // Add personalisation add-ons based on active mode
      if (activeMode === 'player' && player) {
        const playerProductId = document.querySelector('.product-addon-player-item')?.dataset?.productId;
        if (playerProductId) {
          itemsToAdd.push({
            id: parseInt(playerProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'player'
            }
          });
        }
      } else if (activeMode === 'naam-nummer') {
        if (naam) {
          const naamProductId = document.querySelector('.naam-input')?.dataset?.productId;
          if (naamProductId) {
            itemsToAdd.push({
              id: parseInt(naamProductId),
              quantity: 1,
              properties: {
                _groupUID: groupUID,
                _isAddon: 'true',
                _addonType: 'naam'
              }
            });
          }
        }
        if (nummer) {
          const nummerProductId = document.querySelector('.nummer-input')?.dataset?.productId;
          if (nummerProductId) {
            itemsToAdd.push({
              id: parseInt(nummerProductId),
              quantity: 1,
              properties: {
                _groupUID: groupUID,
                _isAddon: 'true',
                _addonType: 'nummer'
              }
            });
          }
        }
      }

      // Add academy initials add-on (if present)
      if (initialsToggle === 'yes' && initials) {
        const initialsProductId = document.querySelector('.product-academy-personalisation-initials-input')?.dataset?.productId;
        if (initialsProductId) {
          itemsToAdd.push({
            id: parseInt(initialsProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'initials'
            }
          });
        }
      }

      // Add all items to cart
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.description || 'Cart addition failed');
      }

      // Open cart drawer and refresh
      this.openCartDrawerAndRefresh();
    },

    openCartDrawerAndRefresh: function() {
      const cartButton = document.querySelector('.js-drawer-open-cart');
      if (cartButton) {
        cartButton.click();
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }, 1500);
      } else {
        window.location.href = '/cart';
      }
    },

    sortCartItems: function() {
      const cartContainer = document.querySelector('.cart__items, [data-products]');
      if (!cartContainer) return;

      const items = Array.from(cartContainer.children);
      if (items.length === 0) return;

      // Separate main items, addons, and ungrouped items
      const mainItems = [];
      const addonMap = new Map(); // groupUID -> array of addon items
      const ungroupedItems = [];

      items.forEach(item => {
        const groupUID = item.getAttribute('data-group-uid');
        const isMain = item.classList.contains('cart__item--main');
        const isAddon = item.classList.contains('cart__item--addon');

        if (isMain && groupUID) {
          mainItems.push({ item, groupUID });
        } else if (isAddon && groupUID) {
          if (!addonMap.has(groupUID)) {
            addonMap.set(groupUID, []);
          }
          addonMap.get(groupUID).push(item);
        } else {
          ungroupedItems.push(item);
        }
      });

      // Clear container
      cartContainer.innerHTML = '';

      // Re-append: main items first with their addons, then ungrouped items
      mainItems.forEach(({ item, groupUID }) => {
        cartContainer.appendChild(item);
        const addons = addonMap.get(groupUID) || [];
        addons.forEach(addon => cartContainer.appendChild(addon));
      });

      ungroupedItems.forEach(item => cartContainer.appendChild(item));
    },

    initCartRemoval: function() {
      // Use event delegation to handle dynamically loaded cart items
      // Remove old listeners first
      const existingHandler = document._cartRemoveHandler;
      if (existingHandler) {
        document.removeEventListener('click', existingHandler, true);
      }

      const existingQtyHandler = document._cartQtyHandler;
      if (existingQtyHandler) {
        document.removeEventListener('click', existingQtyHandler, true);
      }

      // Create handler for remove links
      document._cartRemoveHandler = async (e) => {
        // Check if clicked element is a remove link
        const link = e.target.closest('.cart__remove-link');
        if (!link) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const itemKey = link.getAttribute('data-item-key');
        const groupUID = link.getAttribute('data-group-uid');

        if (!itemKey) return;

        // If it's a grouped item, remove all items in the group
        if (groupUID) {
          try {
            // First, get current cart to find all items with this groupUID
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            // Find all items with the same groupUID
            const itemsToRemove = cart.items.filter(item => {
              const metadata = ProductPersonalisation.getItemMetadata(item);
              return metadata.groupUID === groupUID;
            });

            if (itemsToRemove.length === 0) {
              // Fallback to normal removal
              window.location.href = link.href;
              return;
            }

            // Remove all items in the group
            await ProductPersonalisation.removeGroupItems(groupUID);

            // Refresh cart - if on cart page, reload; otherwise refresh drawer
            if (window.location.pathname.includes('/cart')) {
              // Force a hard reload to ensure checkout sees the updated cart
              window.location.href = window.location.href.split('?')[0];
            } else {
              // For drawer, trigger cart rebuild
              document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
              // Also trigger cart:updated event
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
              // Wait a bit then re-init removal handlers for the new content
              setTimeout(() => {
                ProductPersonalisation.initCartRemoval();
              }, 300);
            }
          } catch (err) {
            // Fallback to normal removal on error
            window.location.href = link.href;
          }
        } else {
          // Normal removal for ungrouped items
          window.location.href = link.href;
        }
      };

      // Create handler for quantity minus buttons
      document._cartQtyHandler = async (e) => {
        // Check if clicked element is a minus button
        const minusButton = e.target.closest('.js-qty__adjust--minus');
        if (!minusButton) return;

        // Find the quantity input and wrapper
        const wrapper = minusButton.closest('.js-qty__wrapper');
        if (!wrapper) return;

        const input = wrapper.querySelector('.js-qty__num');
        if (!input) return;

        const itemKey = input.getAttribute('data-id');
        if (!itemKey) return;

        // Get current quantity
        const currentQty = parseInt(input.value) || 1;
        
        // If clicking minus would result in 0, check if it's a grouped item
        if (currentQty === 1) {
          // Find the cart item
          const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
          if (!cartItem) return;

          const groupUID = cartItem.getAttribute('data-group-uid');
          const isMain = cartItem.classList.contains('cart__item--main');

          // If it's a main item with a groupUID, handle removal ourselves
          if (isMain && groupUID) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Remove all items in the group
            await ProductPersonalisation.removeGroupItems(groupUID);
            return;
          }
        }
      };

      // Attach event listeners with capture phase to catch before default behavior
      document.addEventListener('click', document._cartRemoveHandler, true);
      document.addEventListener('click', document._cartQtyHandler, true);
    },

    clearCart: async function() {
      // Clear entire cart - useful for testing
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Remove all items
        const removePromises = cart.items.map(item => {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        });

        await Promise.all(removePromises);
        
        // Refresh
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }
        
      } catch (err) {
        alert('Error clearing cart. Please try again.');
      }
    },

    removeGroupItems: async function(groupUID) {
      // Helper function to remove all items in a group
      try {
        // Get current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find all items with the same groupUID
        const itemsToRemove = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.groupUID === groupUID;
        });

        if (itemsToRemove.length === 0) return;

        // Remove all items sequentially
        for (const item of itemsToRemove) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Silently continue - error logged but don't block other removals
          }
        }

        // Wait for Shopify to process
        await new Promise(resolve => setTimeout(resolve, 800));

        // Refresh cart
        if (window.location.pathname.includes('/cart')) {
          window.location.href = window.location.href.split('?')[0];
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          setTimeout(() => {
            ProductPersonalisation.initCartRemoval();
          }, 300);
        }
      } catch (err) {
        // Error handled silently - cart will refresh naturally
      }
    },

    cleanupOrphanedAddons: async function() {
      // Remove addons that don't have a matching main item
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const groupUIDs = new Set();

        // Find all main items and their groupUIDs
        cart.items.forEach(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          if (metadata.isMain && metadata.groupUID) {
            groupUIDs.add(metadata.groupUID);
          }
        });

        // Find orphaned addons (addons without a main item)
        const orphanedAddons = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.isAddon && metadata.groupUID && !groupUIDs.has(metadata.groupUID);
        });

        // Remove orphaned addons
        if (orphanedAddons.length > 0) {
          const removePromises = orphanedAddons.map(item => {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
          });

          await Promise.all(removePromises);
          
          // Wait a bit for Shopify to process
          await new Promise(resolve => setTimeout(resolve, 300));
          
          if (window.location.pathname.includes('/cart')) {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          }
        }
      } catch (err) {
        // Error handled silently
      }
    }
  };

    // Initialize on DOM ready
    document.addEventListener('DOMContentLoaded', () => {
      ProductPersonalisation.init();

      // Initialize cart removal handlers on page load
      setTimeout(() => {
        ProductPersonalisation.initCartRemoval();
        ProductPersonalisation.cleanupOrphanedAddons();
      }, 500);

      // Add clear cart function to window for testing (can be called from console)
      window.clearCart = () => {
        if (confirm('Are you sure you want to clear the entire cart? This cannot be undone.')) {
          ProductPersonalisation.clearCart();
        }
      };

    // Re-initialize on Shopify section reloads
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        ProductPersonalisation.init();
        ProductPersonalisation.initCartRemoval();
      }, 100);
    });

    // Sort cart items when cart is built
    document.addEventListener('cart:build', () => {
      setTimeout(() => {
        ProductPersonalisation.sortCartItems();
        ProductPersonalisation.initCartRemoval();
        ProductPersonalisation.cleanupOrphanedAddons();
      }, 200);
    });

  });

})();

